import { useEffect, useState } from 'react';

import { isRunningInNativeApp, native } from '@lemoment/shared';

interface KeyboardState {
  visible: boolean;
  height: number;
}

/**
 * Detect keyboard visibility.
 *
 * Native app: receives `keyboardStateChanged` notifications from the
 * native shell. Browser: uses `visualViewport` as fallback.
 */
export const useKeyboardLayout = (): KeyboardState => {
  const [keyboard, setKeyboard] = useState<KeyboardState>({
    visible: false,
    height: 0,
  });

  useEffect(() => {
    const updateCSSVariable = (height: number) => {
      document.documentElement.style.setProperty(
        '--keyboard-height',
        `${height}px`,
      );
      document.documentElement.classList.toggle('keyboard-visible', height > 0);
    };

    if (isRunningInNativeApp()) {
      return native.on('keyboardStateChanged', (payload) => {
        const { visible, height } = payload;
        setKeyboard({ visible, height });
        updateCSSVariable(height);
      });
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const handleResize = () => {
      const diff = window.innerHeight - viewport.height;
      const isVisible = diff > 100;
      const height = isVisible ? diff : 0;
      setKeyboard({ visible: isVisible, height });
      updateCSSVariable(height);
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  return keyboard;
};
